import { useState, useRef, useEffect } from "react";
import {
    ArrowLeft, RefreshCw, Plus, Check, X, Pencil, Save,
    ChevronRight, ChevronDown, Search, Building2, Users,
    ShieldCheck, Clock
} from "lucide-react";
import { useGlobal } from '../../../../contexts';
import {
    addStockApprovel,
    approvalStatus,
    deactivateDealerChat,
    deleteDealerChat,
    getreceiveApprovel,
    getsyncApproval,
    updateReceivedRequest
} from "../../../../redux/actions/utility";
import { useDispatch, useSelector } from "react-redux";
import { useUser } from 'contexts';

// ─── Trash Icon (inline SVG — no import needed) ───────────────────────────────
const TrashIcon = ({ size = 14, color = "currentColor" }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size} height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
);

// ─── Skeleton Loader ──────────────────────────────────────────────────────────
const shimmerStyle = `
    @keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
    }
`;

const SkeletonRow = ({ cols = 5 }) => (
    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
        {Array.from({ length: cols }).map((_, i) => (
            <td key={i} style={{ padding: "14px 20px" }}>
                <div style={{
                    height: i === 0 ? 14 : i === cols - 1 ? 28 : 18,
                    width: i === 0 ? 24 : i === cols - 1 ? 80 : "65%",
                    borderRadius: 6,
                    background: "linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.4s infinite",
                }} />
            </td>
        ))}
    </tr>
);

const TableLoader = ({ cols = 5, rows = 4 }) => (
    <>
        <style>{shimmerStyle}</style>
        <tbody>
            {Array.from({ length: rows }).map((_, i) => (
                <SkeletonRow key={i} cols={cols} />
            ))}
        </tbody>
    </>
);

// ─── Multi-Select Dropdown ────────────────────────────────────────────────────
const MultiSelectDropdown = ({ options = [], value = [], onChange, placeholder = "Select..." }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
    const allSelected = filtered.length > 0 && filtered.every(o => value.includes(o.value));
    const someSelected = filtered.some(o => value.includes(o.value));

    const toggleAll = () => {
        if (allSelected) {
            const fv = filtered.map(o => o.value);
            onChange(value.filter(v => !fv.includes(v)));
        } else {
            const fv = filtered.map(o => o.value);
            onChange([...new Set([...value, ...fv])]);
        }
    };

    const toggleOne = (val) => {
        onChange(value.includes(val) ? value.filter(v => v !== val) : [...value, val]);
    };

    const selectedLabels = options.filter(o => value.includes(o.value)).map(o => o.label);

    return (
        <div ref={ref} style={{ position: "relative", width: "100%" }}>
            <button
                type="button"
                onClick={() => setOpen(p => !p)}
                style={{
                    width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 8, padding: "10px 14px", borderRadius: 12,
                    border: "1.5px solid #e2e8f0", background: "#f8fafc",
                    fontSize: 13, color: "#374151", cursor: "pointer",
                    outline: "none", fontFamily: "'DM Sans', sans-serif",
                }}
            >
                <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {selectedLabels.length === 0
                        ? <span style={{ color: "#94a3b8" }}>{placeholder}</span>
                        : selectedLabels.length === options.length
                            ? <span style={{ color: "#2563eb", fontWeight: 600 }}>All Companies Selected</span>
                            : <span>{selectedLabels.slice(0, 2).join(", ")}{selectedLabels.length > 2 ? ` +${selectedLabels.length - 2} more` : ""}</span>
                    }
                </span>
                <ChevronDown size={16} style={{ flexShrink: 0, color: "#94a3b8", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
            </button>

            {open && (
                <div style={{
                    position: "absolute", zIndex: 999, marginTop: 6, width: "100%",
                    borderRadius: 14, border: "1.5px solid #e2e8f0",
                    background: "#fff", boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
                    overflow: "hidden", maxHeight: 280,
                }}>
                    <div style={{ padding: "10px 12px", borderBottom: "1px solid #f1f5f9" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f8fafc", borderRadius: 8, padding: "6px 10px" }}>
                            <Search size={13} style={{ color: "#94a3b8", flexShrink: 0 }} />
                            <input
                                autoFocus value={search} onChange={e => setSearch(e.target.value)}
                                placeholder="Search companies..."
                                style={{ flex: 1, border: "none", background: "transparent", outline: "none", fontSize: 12, color: "#374151", fontFamily: "'DM Sans', sans-serif" }}
                            />
                        </div>
                    </div>
                    <div style={{ overflowY: "auto", maxHeight: 210 }}>
                        <button
                            type="button" onClick={toggleAll}
                            style={{
                                width: "100%", display: "flex", alignItems: "center", gap: 10,
                                padding: "10px 14px", fontSize: 12, fontWeight: 700,
                                borderBottom: "1px solid #f1f5f9", background: someSelected ? "#eff6ff" : "transparent",
                                color: "#2563eb", cursor: "pointer", border: "none",
                                fontFamily: "'DM Sans', sans-serif",
                            }}
                        >
                            <span style={{
                                width: 16, height: 16, borderRadius: 4, border: allSelected ? "none" : "2px solid #94a3b8",
                                background: allSelected ? "#2563eb" : someSelected ? "#bfdbfe" : "transparent",
                                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                            }}>
                                {allSelected && <Check size={10} color="#fff" />}
                                {someSelected && !allSelected && <span style={{ width: 8, height: 2, background: "#2563eb", borderRadius: 2 }} />}
                            </span>
                            Select All
                            {someSelected && !allSelected && (
                                <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>{value.length} selected</span>
                            )}
                        </button>
                        {filtered.length === 0
                            ? <p style={{ padding: "16px 14px", textAlign: "center", fontSize: 12, color: "#94a3b8" }}>No companies found</p>
                            : filtered.map(opt => {
                                const isChecked = value.includes(opt.value);
                                return (
                                    <button
                                        key={opt.value} type="button" onClick={() => toggleOne(opt.value)}
                                        style={{
                                            width: "100%", display: "flex", alignItems: "center", gap: 10,
                                            padding: "10px 14px", fontSize: 13,
                                            background: isChecked ? "#eff6ff" : "transparent",
                                            color: isChecked ? "#1d4ed8" : "#374151",
                                            cursor: "pointer", border: "none",
                                            fontFamily: "'DM Sans', sans-serif",
                                        }}
                                    >
                                        <span style={{
                                            width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                                            border: isChecked ? "none" : "2px solid #d1d5db",
                                            background: isChecked ? "#2563eb" : "transparent",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                        }}>
                                            {isChecked && <Check size={10} color="#fff" />}
                                        </span>
                                        {opt.label}
                                    </button>
                                );
                            })
                        }
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Modal ────────────────────────────────────────────────────────────────────
const Modal = ({ isOpen, onClose, children }) => {
    useEffect(() => {
        if (isOpen) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "";
        return () => { document.body.style.overflow = ""; };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: "fixed", inset: 0, zIndex: 1000,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "rgba(15,23,42,0.4)", backdropFilter: "blur(6px)",
            }}
            onClick={onClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: "#fff", borderRadius: 20, width: "100%", maxWidth: 440,
                    margin: 16, boxShadow: "0 32px 64px rgba(0,0,0,0.18)",
                    border: "1.5px solid #e2e8f0",
                }}
            >
                {children}
            </div>
        </div>
    );
};

// ─── Confirm Delete Modal ─────────────────────────────────────────────────────
const ConfirmDeleteModal = ({ isOpen, onClose, onConfirm }) => {
    if (!isOpen) return null;
    return (
        <div
            style={{
                position: "fixed", inset: 0, zIndex: 1100,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "rgba(15,23,42,0.5)", backdropFilter: "blur(6px)",
            }}
            onClick={onClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: "#fff", borderRadius: 20, width: "100%", maxWidth: 380,
                    margin: 16, boxShadow: "0 32px 64px rgba(0,0,0,0.18)",
                    border: "1.5px solid #fecdd3", padding: 28,
                    display: "flex", flexDirection: "column", gap: 20,
                }}
            >
                {/* Icon */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                    <div style={{
                        width: 56, height: 56, borderRadius: 16,
                        background: "#fff1f2", border: "1.5px solid #fecdd3",
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <TrashIcon size={24} color="#e11d48" />
                    </div>
                    <p style={{ margin: 0, fontWeight: 800, fontSize: 17, color: "#0f172a", textAlign: "center" }}>
                        Delete Entry?
                    </p>
                    <p style={{ margin: 0, fontSize: 13, color: "#94a3b8", textAlign: "center", lineHeight: 1.6 }}>
                        This action cannot be undone. The record will be permanently removed.
                    </p>
                </div>
                {/* Buttons */}
                <div style={{ display: "flex", gap: 10 }}>
                    <button
                        onClick={onClose}
                        style={{
                            flex: 1, padding: "11px", borderRadius: 12, fontSize: 13, fontWeight: 700,
                            border: "1.5px solid #e2e8f0", background: "transparent", color: "#64748b",
                            cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        style={{
                            flex: 1, padding: "11px", borderRadius: 12, fontSize: 13, fontWeight: 700,
                            border: "none", background: "linear-gradient(135deg,#e11d48,#f43f5e)",
                            color: "#fff", cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                            boxShadow: "0 4px 14px rgba(225,29,72,0.3)",
                        }}
                    >
                        Yes, Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Tab 1: Sync Codes ────────────────────────────────────────────────────────
const SyncCodesTab = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [form, setForm] = useState({ syncCode: "", companies: [] });
    const [error, setError] = useState("");
    const [deleteId, setDeleteId] = useState(null);
    const [loading, setLoading] = useState(false);

    const syncApprovel = useSelector((state) => state?.utility?.syncApprovel);
    const list = (syncApprovel ?? []).filter(item => item.status === true);

    const { companyList } = useGlobal();
    const { user } = useUser();
    const dispatch = useDispatch();

    const allCompanyOptions = companyList
        ?.filter((c) => !c.isShared && c.setting?.syncCode?.trim())
        .map((c) => ({
            label: c.name,
            value: c.id,
            senderSyncCode: c.setting?.syncCode ?? "",
        })) ?? [];

    const alreadyLinkedReceiveSyncCodes = new Set(list.map((item) => item.senderSyncCode));

    const companyOptions = form.syncCode.trim()
        ? allCompanyOptions.filter((c) => !alreadyLinkedReceiveSyncCodes.has(c.senderSyncCode))
        : allCompanyOptions;

    useEffect(() => {
        if (user?.code) {
            setLoading(true);
            dispatch(getsyncApproval({ senderDbName: user?.code }))
                .finally(() => setLoading(false));
        }
    }, [dispatch, user?.code]);

    const handleAdd = async () => {
        if (!form.syncCode.trim()) { setError("Sync Code is required."); return; }
        if (form.companies.length === 0) { setError("Please select at least one company."); return; }

        const syncPairs = form.companies.map((companyId) => {
            const company = allCompanyOptions.find((c) => c.value === companyId);
            return {
                receiverSyncCode: form.syncCode.trim(),
                senderSyncCode: company?.senderSyncCode ?? "",
            };
        });

        await dispatch(addStockApprovel(syncPairs));
        dispatch(getsyncApproval({ senderDbName: user?.code }));
        setForm({ syncCode: "", companies: [] });
        setError("");
        setIsOpen(false);
    };

    const handleDeleteConfirm = async () => {
        if (deleteId) {
            await dispatch(deactivateDealerChat(deleteId));
            setDeleteId(null);
        }
    };

    const handleClose = () => {
        setForm({ syncCode: "", companies: [] });
        setError("");
        setIsOpen(false);
    };

    const approved = list.filter(item => item.isApprove).length;
    const pending = list.filter(item => !item.isApprove).length;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Confirm Delete Modal */}
            <ConfirmDeleteModal
                isOpen={deleteId !== null}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDeleteConfirm}
            />

            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#94a3b8" }}>
                        Sync Entries
                    </span>
                    <span style={{
                        fontSize: 11, fontWeight: 700, background: "linear-gradient(135deg,#2563eb,#0ea5e9)",
                        color: "#fff", padding: "2px 10px", borderRadius: 20,
                    }}>{list.length}</span>
                    <span style={{
                        display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
                        background: "#f0fdf4", color: "#16a34a", padding: "5px 12px", borderRadius: 20,
                        border: "1px solid #bbf7d0",
                    }}>
                        <ShieldCheck size={12} /> {approved} Approved
                    </span>
                    <span style={{
                        display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
                        background: "#fff7ed", color: "#ea580c", padding: "5px 12px", borderRadius: 20,
                        border: "1px solid #fed7aa",
                    }}>
                        <Clock size={12} /> {pending} Pending
                    </span>
                </div>
                <button
                    onClick={() => setIsOpen(true)}
                    style={{
                        display: "flex", alignItems: "center", gap: 6,
                        background: "linear-gradient(135deg,#2563eb,#0ea5e9)",
                        color: "#fff", border: "none", borderRadius: 10,
                        padding: "8px 16px", fontSize: 13, fontWeight: 700,
                        cursor: "pointer", boxShadow: "0 4px 14px rgba(37,99,235,0.35)",
                        fontFamily: "'DM Sans', sans-serif",
                    }}
                >
                    <Plus size={15} /> Add New
                </button>
            </div>

            {/* Table */}
            <div style={{ borderRadius: 16, border: "1.5px solid #e2e8f0", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: "#f8fafc" }}>
                            {["#", "Sender Company", "Receiver Company", "Status", "Actions"].map(h => (
                                <th key={h} style={{
                                    textAlign: "left", padding: "12px 20px",
                                    fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                                    letterSpacing: "0.1em", color: "#94a3b8",
                                    borderBottom: "1.5px solid #e2e8f0", whiteSpace: "nowrap",
                                }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    {loading ? (
                        <TableLoader cols={5} rows={4} />
                    ) : (
                        <tbody>
                            {list.length === 0 ? (
                                <tr>
                                    <td colSpan={5} style={{ padding: "40px 20px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                                        No sync entries yet. Click "Add New" to get started.
                                    </td>
                                </tr>
                            ) : list.map((item, i) => (
                                <tr
                                    key={item.id}
                                    style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.15s" }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                >
                                    <td style={{ padding: "14px 20px", color: "#cbd5e1", fontSize: 11, fontFamily: "monospace" }}>
                                        {i + 1}
                                    </td>
                                    <td style={{ padding: "14px 20px" }}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                <Building2 size={13} style={{ color: "#94a3b8", flexShrink: 0 }} />
                                                <span style={{ fontWeight: 600, color: "#1e293b" }}>{item.senderCompanyName ?? "—"}</span>
                                            </div>
                                            <span style={{ fontSize: 11, color: "#94a3b8", paddingLeft: 19 }}>{item.senderSyncCode}</span>
                                        </div>
                                    </td>
                                    <td style={{ padding: "14px 20px" }}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                <Building2 size={13} style={{ color: "#94a3b8", flexShrink: 0 }} />
                                                <span style={{ fontWeight: 600, color: "#1e293b" }}>{item.receiverCompanyName ?? "—"}</span>
                                            </div>
                                            <span style={{ fontSize: 11, color: "#94a3b8", paddingLeft: 19 }}>{item.receiveSyncCode}</span>
                                        </div>
                                    </td>
                                    <td style={{ padding: "14px 20px" }}>
                                        <span style={{
                                            display: "inline-flex", alignItems: "center", gap: 5,
                                            fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20,
                                            background: item.isApprove ? "#f0fdf4" : "#fff7ed",
                                            color: item.isApprove ? "#16a34a" : "#ea580c",
                                            border: `1px solid ${item.isApprove ? "#bbf7d0" : "#fed7aa"}`,
                                        }}>
                                            {item.isApprove
                                                ? <><ShieldCheck size={11} /> Approved</>
                                                : <><Clock size={11} /> Pending</>
                                            }
                                        </span>
                                    </td>
                                    {/* ✅ Delete Button */}
                                    <td style={{ padding: "14px 20px" }}>
                                        <button
                                            onClick={() => setDeleteId(item.id)}
                                            style={{
                                                display: "inline-flex", alignItems: "center", gap: 6,
                                                fontSize: 11, fontWeight: 600, padding: "6px 12px",
                                                borderRadius: 8, background: "#fff1f2",
                                                border: "1.5px solid #fecdd3", color: "#e11d48",
                                                cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                                            }}
                                        >
                                            <TrashIcon size={12} color="#e11d48" /> Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    )}
                </table>
            </div>

            {/* Add Modal */}
            <Modal isOpen={isOpen} onClose={handleClose}>
                <div style={{ padding: "24px 24px 0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
                        <div style={{ padding: 8, background: "linear-gradient(135deg,#2563eb,#0ea5e9)", borderRadius: 12 }}>
                            <Plus size={16} color="#fff" />
                        </div>
                        <div>
                            <p style={{ margin: 0, fontWeight: 800, fontSize: 16, color: "#1e293b" }}>New Sync Entry</p>
                            <p style={{ margin: 0, fontSize: 12, color: "#94a3b8", marginTop: 2 }}>Fill in the sync code details</p>
                        </div>
                        <button onClick={handleClose} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 4 }}>
                            <X size={18} />
                        </button>
                    </div>
                </div>
                <div style={{ padding: "20px 24px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#64748b" }}>
                            Sync Code
                        </label>
                        <input
                            type="text" value={form.syncCode}
                            onChange={e => { setForm({ ...form, syncCode: e.target.value }); setError(""); }}
                            placeholder="e.g. SYN-004"
                            style={{
                                padding: "10px 14px", borderRadius: 12, border: "1.5px solid #e2e8f0",
                                fontSize: 13, color: "#1e293b", outline: "none", background: "#f8fafc",
                                fontFamily: "'DM Sans', sans-serif",
                            }}
                            onFocus={e => e.target.style.borderColor = "#2563eb"}
                            onBlur={e => e.target.style.borderColor = "#e2e8f0"}
                        />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#64748b" }}>
                            Company
                        </label>
                        <MultiSelectDropdown
                            options={companyOptions} value={form.companies}
                            onChange={val => { setForm({ ...form, companies: val }); setError(""); }}
                            placeholder="Select company"
                        />
                        {form.companies.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                                {companyOptions.filter(c => form.companies.includes(c.value)).map(c => (
                                    <span key={c.value} style={{
                                        display: "inline-flex", alignItems: "center", gap: 4,
                                        fontSize: 11, fontWeight: 600, padding: "3px 10px",
                                        borderRadius: 20, background: "#eff6ff", color: "#2563eb",
                                        border: "1px solid #bfdbfe",
                                    }}>
                                        {c.label}
                                        <button type="button"
                                            onClick={() => setForm({ ...form, companies: form.companies.filter(v => v !== c.value) })}
                                            style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 0, lineHeight: 1 }}
                                        >
                                            <X size={11} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    {error && (
                        <p style={{ margin: 0, fontSize: 12, color: "#ef4444", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                            <X size={12} /> {error}
                        </p>
                    )}
                    <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                        <button onClick={handleClose} style={{
                            flex: 1, padding: "11px", borderRadius: 12, fontSize: 13, fontWeight: 700,
                            border: "1.5px solid #e2e8f0", background: "transparent", color: "#64748b",
                            cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                        }}>Cancel</button>
                        <button onClick={handleAdd} style={{
                            flex: 1, padding: "11px", borderRadius: 12, fontSize: 13, fontWeight: 700,
                            border: "none", background: "linear-gradient(135deg,#2563eb,#0ea5e9)",
                            color: "#fff", cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                            boxShadow: "0 4px 14px rgba(37,99,235,0.3)",
                        }}>Save Entry</button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

// ─── Tab 2: Users / Received Requests ─────────────────────────────────────────
const UsersTab = () => {
    const dispatch = useDispatch();
    const { user } = useUser();
    const receiveApprovel = useSelector((state) => state?.utility?.receiveApprovel);
    const list = (receiveApprovel ?? []).filter(item => item.status === true);
    const [editingId, setEditingId] = useState(null);
    const [editValues, setEditValues] = useState({ name: "", contactMobile: "" });
    const [deleteId, setDeleteId] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user?.code) {
            setLoading(true);
            dispatch(getreceiveApprovel({ dbCode: user?.code }))
                .finally(() => setLoading(false));
        }
    }, [dispatch, user?.code]);

    const startEdit = (item) => {
        setEditingId(item.id);
        setEditValues({ name: item.senderCompanyName ?? "", contactMobile: item.senderMobile ?? "" });
    };

    const saveEdit = async (item) => {
        await dispatch(updateReceivedRequest({
            senderDbName: item.senderDbName,
            name: editValues.name,
            contactMobile: editValues.contactMobile,
        }));
        dispatch(getreceiveApprovel({ dbCode: user?.code }));
        setEditingId(null);
    };

    const toggleApproval = async (item) => {
        await dispatch(approvalStatus({ id: item.id, isApprove: !item.isApprove }));
        dispatch(getreceiveApprovel({ dbCode: user?.code }));
    };

    const handleDeleteConfirm = async () => {
        if (deleteId) {
            await dispatch(deactivateDealerChat(deleteId));
            setDeleteId(null);
        }
    };

    const approved = list.filter(u => u.isApprove).length;
    const pending = list.filter(u => !u.isApprove).length;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Confirm Delete Modal */}
            <ConfirmDeleteModal
                isOpen={deleteId !== null}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDeleteConfirm}
            />

            {/* Stats */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#94a3b8" }}>
                    Received Requests
                </span>
                <span style={{
                    fontSize: 11, fontWeight: 700,
                    background: "linear-gradient(135deg,#2563eb,#0ea5e9)",
                    color: "#fff", padding: "2px 10px", borderRadius: 20,
                }}>{list.length}</span>
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    <span style={{
                        display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
                        background: "#f0fdf4", color: "#16a34a", padding: "5px 12px", borderRadius: 20,
                        border: "1px solid #bbf7d0",
                    }}>
                        <ShieldCheck size={12} /> {approved} Approved
                    </span>
                    <span style={{
                        display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
                        background: "#fff7ed", color: "#ea580c", padding: "5px 12px", borderRadius: 20,
                        border: "1px solid #fed7aa",
                    }}>
                        <Clock size={12} /> {pending} Pending
                    </span>
                </div>
            </div>

            {/* Table */}
            <div style={{ borderRadius: 16, border: "1.5px solid #e2e8f0", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: "#f8fafc" }}>
                            {["#", "Company Name", "Mobile No.", "Status", "Actions"].map(h => (
                                <th key={h} style={{
                                    textAlign: "left", padding: "12px 20px",
                                    fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                                    letterSpacing: "0.1em", color: "#94a3b8",
                                    borderBottom: "1.5px solid #e2e8f0",
                                }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    {loading ? (
                        <TableLoader cols={5} rows={4} />
                    ) : (
                        <tbody>
                            {list.length === 0 ? (
                                <tr>
                                    <td colSpan={5} style={{ padding: "40px 20px", textAlign: "center", color: "#94a3b8" }}>
                                        No received requests yet.
                                    </td>
                                </tr>
                            ) : list.map((item, i) => {
                                const isEditing = editingId === item.id;
                                return (
                                    <tr
                                        key={item.id}
                                        style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.15s" }}
                                        onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                    >
                                        <td style={{ padding: "14px 20px", color: "#cbd5e1", fontSize: 11 }}>{i + 1}</td>

                                        {/* Company Name */}
                                        <td style={{ padding: "14px 20px" }}>
                                            {isEditing ? (
                                                <input
                                                    autoFocus
                                                    value={editValues.name}
                                                    onChange={e => setEditValues({ ...editValues, name: e.target.value })}
                                                    style={{
                                                        padding: "7px 12px", borderRadius: 10,
                                                        border: "1.5px solid #2563eb", fontSize: 13,
                                                        color: "#1e293b", outline: "none", background: "#eff6ff",
                                                        fontFamily: "'DM Sans', sans-serif", width: "100%",
                                                    }}
                                                />
                                            ) : (
                                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    <div style={{
                                                        width: 32, height: 32, borderRadius: 10,
                                                        background: `hsl(${(i * 60) % 360}, 70%, 92%)`,
                                                        color: `hsl(${(i * 60) % 360}, 70%, 35%)`,
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                        fontSize: 11, fontWeight: 800, flexShrink: 0,
                                                    }}>
                                                        {item.senderCompanyName?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() ?? "??"}
                                                    </div>
                                                    <span style={{ fontWeight: 600, color: "#1e293b" }}>
                                                        {item.senderCompanyName ?? "—"}
                                                    </span>
                                                </div>
                                            )}
                                        </td>

                                        {/* Mobile */}
                                        <td style={{ padding: "14px 20px" }}>
                                            {isEditing ? (
                                                <input
                                                    value={editValues.contactMobile}
                                                    onChange={e => setEditValues({ ...editValues, contactMobile: e.target.value })}
                                                    style={{
                                                        padding: "7px 12px", borderRadius: 10,
                                                        border: "1.5px solid #2563eb", fontSize: 12,
                                                        color: "#1e293b", outline: "none", background: "#eff6ff",
                                                        fontFamily: "monospace", width: "100%",
                                                    }}
                                                />
                                            ) : (
                                                <span style={{ fontFamily: "monospace", fontSize: 12, color: "#64748b" }}>
                                                    {item.senderMobile ?? "—"}
                                                </span>
                                            )}
                                        </td>

                                        {/* Status Toggle */}
                                        <td style={{ padding: "14px 20px" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <button
                                                    onClick={() => toggleApproval(item)}
                                                    style={{
                                                        width: 44, height: 24, borderRadius: 12, border: "none",
                                                        background: item.isApprove
                                                            ? "linear-gradient(135deg,#22c55e,#16a34a)"
                                                            : "#e2e8f0",
                                                        position: "relative", cursor: "pointer", transition: "all 0.3s",
                                                        boxShadow: item.isApprove ? "0 2px 8px rgba(34,197,94,0.35)" : "none",
                                                    }}
                                                >
                                                    <span style={{
                                                        position: "absolute", top: 3,
                                                        left: item.isApprove ? 23 : 3,
                                                        width: 18, height: 18, borderRadius: 9,
                                                        background: "#fff",
                                                        boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                                                        transition: "left 0.3s",
                                                    }} />
                                                </button>
                                                <span style={{
                                                    fontSize: 11, fontWeight: 700,
                                                    color: item.isApprove ? "#16a34a" : "#94a3b8",
                                                }}>
                                                    {item.isApprove ? "Approved" : "Pending"}
                                                </span>
                                            </div>
                                        </td>

                                        {/* Actions */}
                                        <td style={{ padding: "14px 20px" }}>
                                            {isEditing ? (
                                                <div style={{ display: "flex", gap: 6 }}>
                                                    <button onClick={() => saveEdit(item)} style={{
                                                        display: "flex", alignItems: "center", gap: 4,
                                                        fontSize: 11, fontWeight: 700, padding: "6px 12px",
                                                        borderRadius: 8, background: "#2563eb", color: "#fff",
                                                        border: "none", cursor: "pointer",
                                                        fontFamily: "'DM Sans', sans-serif",
                                                    }}>
                                                        <Save size={13} /> Save
                                                    </button>
                                                    <button onClick={() => setEditingId(null)} style={{
                                                        fontSize: 11, fontWeight: 600, padding: "6px 12px",
                                                        borderRadius: 8, background: "transparent",
                                                        border: "1.5px solid #e2e8f0", color: "#64748b",
                                                        cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                                                    }}>
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <div style={{ display: "flex", gap: 6 }}>
                                                    {/* Edit */}
                                                    <button onClick={() => startEdit(item)} style={{
                                                        display: "inline-flex", alignItems: "center", gap: 4,
                                                        fontSize: 11, fontWeight: 600, padding: "6px 12px",
                                                        borderRadius: 8, background: "#eff6ff",
                                                        border: "1.5px solid #bfdbfe", color: "#2563eb",
                                                        cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                                                    }}>
                                                        <Pencil size={12} /> Edit
                                                    </button>
                                                    {/* ✅ Delete */}
                                                    <button onClick={() => setDeleteId(item.id)} style={{
                                                        display: "inline-flex", alignItems: "center", gap: 6,
                                                        fontSize: 11, fontWeight: 600, padding: "6px 12px",
                                                        borderRadius: 8, background: "#fff1f2",
                                                        border: "1.5px solid #fecdd3", color: "#e11d48",
                                                        cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                                                    }}>
                                                        <TrashIcon size={12} color="#e11d48" /> Delete
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    )}
                </table>
            </div>
        </div>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function StockApproval() {
    const [activeTab, setActiveTab] = useState(0);
    const tabs = [
        { label: "Sync Codes", icon: RefreshCw },
        { label: "Unapproved Entries", icon: Users },
    ];

    return (
        <div style={{
            minHeight: "100vh",
            background: "linear-gradient(135deg,#f0f4ff 0%,#f8fafc 50%,#f0f9ff 100%)",
            padding: "40px 16px",
            marginTop: 20,
        }}>
            <div style={{ width: "100%" }}>

                {/* Page Header */}
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
                    <button
                        style={{
                            padding: 10, borderRadius: 12, border: "1.5px solid #e2e8f0",
                            background: "#fff", cursor: "pointer", display: "flex", alignItems: "center",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        }}
                        onClick={() => window.history.back()}
                    >
                        <ArrowLeft size={18} style={{ color: "#64748b" }} />
                    </button>

                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{
                            padding: 10,
                            background: "linear-gradient(135deg,#2563eb,#0ea5e9)",
                            borderRadius: 14, boxShadow: "0 6px 20px rgba(37,99,235,0.3)",
                        }}>
                            <RefreshCw size={20} color="#fff" />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
                                Stock Approval
                            </h1>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                                <span style={{ fontSize: 12, color: "#94a3b8", cursor: "pointer" }}>Utility Tools</span>
                                <ChevronRight size={12} style={{ color: "#cbd5e1" }} />
                                <span style={{ fontSize: 12, color: "#2563eb", fontWeight: 600 }}>Stock Approval</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Card */}
                <div style={{
                    background: "#fff", borderRadius: 20,
                    border: "1.5px solid #e2e8f0",
                    boxShadow: "0 8px 40px rgba(0,0,0,0.07)",
                    overflow: "hidden",
                }}>
                    {/* Tabs */}
                    <div style={{ display: "flex", borderBottom: "1.5px solid #f1f5f9", background: "#fafbfc" }}>
                        {tabs.map(({ label, icon: Icon }, i) => (
                            <button key={label} onClick={() => setActiveTab(i)}
                                style={{
                                    display: "flex", alignItems: "center", gap: 7,
                                    padding: "16px 24px", fontSize: 13, fontWeight: 700,
                                    color: activeTab === i ? "#2563eb" : "#94a3b8",
                                    background: "none", border: "none", cursor: "pointer",
                                    position: "relative", fontFamily: "'DM Sans', sans-serif",
                                }}
                            >
                                <Icon size={15} />
                                {label}
                                {activeTab === i && (
                                    <span style={{
                                        position: "absolute", bottom: 0, left: 0, right: 0, height: 2,
                                        background: "linear-gradient(90deg,#2563eb,#0ea5e9)",
                                        borderRadius: "2px 2px 0 0",
                                    }} />
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Content */}
                    <div style={{ padding: 24 }}>
                        {activeTab === 0 && <SyncCodesTab />}
                        {activeTab === 1 && <UsersTab />}
                    </div>
                </div>
            </div>
        </div>
    );
}